import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import DatePicker from "@/components/DatePicker";
import { myTaskStatusOptions } from "@/components/StatusBadge";
import { Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";

export const MYTASK_CATEGORY_OPTIONS = [
    { value: "professional", label: "Professional" },
    { value: "combined_billing", label: "Combined Billing" },
    { value: "personal", label: "Personal" },
];

const PRIORITY_OPTIONS = [
    { value: "low", label: "Low" },
    { value: "medium", label: "Medium" },
    { value: "high", label: "High" },
];

const emptyFor = (category) => ({
    category: category || "professional",
    title: "",
    work_type: "",
    client_name: "",
    task_date: "",
    deadline_date: "",
    status: "not_initiated",
    priority: "medium",
    remarks: "",
});

export default function MyTaskFormDialog({
    open,
    onOpenChange,
    task,
    defaultCategory,
    onSaved,
}) {
    const [form, setForm] = useState(emptyFor(defaultCategory));
    const [submitting, setSubmitting] = useState(false);
    const [clients, setClients] = useState([]);
    const [confirmDelete, setConfirmDelete] = useState(false);

    useEffect(() => {
        if (!open) return;
        api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
        setForm(
            task
                ? {
                      ...emptyFor(task.category),
                      ...task,
                      client_name: task.client_name || "",
                      task_date: task.task_date || "",
                      deadline_date: task.deadline_date || "",
                      remarks: task.remarks || "",
                  }
                : emptyFor(defaultCategory),
        );
    }, [open, task, defaultCategory]);

    const isPersonal = form.category === "personal";
    const statusOptions = myTaskStatusOptions(form.category);

    const change = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const changeCategory = (v) =>
        setForm((f) => {
            const allowed = myTaskStatusOptions(v).map((o) => o.value);
            return {
                ...f,
                category: v,
                status: allowed.includes(f.status) ? f.status : "not_initiated",
            };
        });

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.title.trim() || !form.work_type.trim()) {
            toast.error("Task and Type of Work are required");
            return;
        }
        setSubmitting(true);
        const payload = {
            category: form.category,
            title: form.title.trim(),
            work_type: form.work_type.trim(),
            client_name: isPersonal ? null : (form.client_name?.trim() || null),
            task_date: form.task_date || null,
            deadline_date: form.deadline_date || null,
            status: form.status,
            priority: form.priority,
            remarks: form.remarks?.trim() || null,
        };
        try {
            if (task?.id) {
                await api.patch(`/my-tasks/${task.id}`, { ...payload, if_match: task.updated_at });
                toast.success("Task updated");
            } else {
                await api.post("/my-tasks", payload);
                toast.success("Task created");
            }
            onSaved?.();
            onOpenChange(false);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    const onDelete = async () => {
        if (!task?.id) return;
        try {
            await api.delete(`/my-tasks/${task.id}`);
            toast.success("Task deleted");
            setConfirmDelete(false);
            onSaved?.();
            onOpenChange(false);
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    return (
        <>
            <Dialog open={open} onOpenChange={onOpenChange}>
                <DialogContent
                    className="max-w-2xl max-h-[90vh] overflow-y-auto"
                    data-testid="mytask-dialog"
                >
                    <DialogHeader>
                        <DialogTitle className="font-display text-2xl font-extrabold tracking-tight">
                            {task ? "Edit task" : "New task"}
                        </DialogTitle>
                        <DialogDescription>
                            Task and Type of Work are required.
                        </DialogDescription>
                    </DialogHeader>

                    <form onSubmit={onSubmit} className="space-y-5">
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                            <div className="space-y-2">
                                <Label>Section</Label>
                                <Select value={form.category} onValueChange={changeCategory}>
                                    <SelectTrigger data-testid="mytask-category-trigger">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {MYTASK_CATEGORY_OPTIONS.map((c) => (
                                            <SelectItem key={c.value} value={c.value}>
                                                {c.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label>Status</Label>
                                <Select
                                    value={form.status}
                                    onValueChange={(v) => change("status", v)}
                                >
                                    <SelectTrigger data-testid="mytask-status-trigger">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {statusOptions.map((s) => (
                                            <SelectItem key={s.value} value={s.value}>
                                                {s.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2 md:col-span-2">
                                <Label htmlFor="mt-title">
                                    Task <span className="text-primary">*</span>
                                </Label>
                                <Input
                                    id="mt-title"
                                    value={form.title}
                                    onChange={(e) => change("title", e.target.value)}
                                    placeholder="e.g. Client Billing - Acme"
                                    data-testid="mytask-title-input"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="mt-work">
                                    Type of Work <span className="text-primary">*</span>
                                </Label>
                                <Input
                                    id="mt-work"
                                    value={form.work_type}
                                    onChange={(e) => change("work_type", e.target.value)}
                                    placeholder="Billing, Filing, Review..."
                                    data-testid="mytask-worktype-input"
                                />
                            </div>
                            {!isPersonal && (
                                <div className="space-y-2">
                                    <Label htmlFor="mt-client">Client</Label>
                                    <Input
                                        id="mt-client"
                                        list="mytask-clients"
                                        value={form.client_name}
                                        onChange={(e) => change("client_name", e.target.value)}
                                        placeholder="Optional"
                                        data-testid="mytask-client-input"
                                    />
                                    <datalist id="mytask-clients">
                                        {clients.map((c) => (
                                            <option key={c.id} value={c.name} />
                                        ))}
                                    </datalist>
                                </div>
                            )}
                            <div className="space-y-2">
                                <Label>Start Date</Label>
                                <DatePicker
                                    value={form.task_date}
                                    onChange={(v) => change("task_date", v)}
                                    placeholder="Select start date"
                                    testId="mytask-start-input"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Due Date</Label>
                                <DatePicker
                                    value={form.deadline_date}
                                    onChange={(v) => change("deadline_date", v)}
                                    placeholder="Select due date"
                                    testId="mytask-due-input"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Priority</Label>
                                <Select
                                    value={form.priority}
                                    onValueChange={(v) => change("priority", v)}
                                >
                                    <SelectTrigger data-testid="mytask-priority-trigger">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {PRIORITY_OPTIONS.map((p) => (
                                            <SelectItem key={p.value} value={p.value}>
                                                {p.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="mt-remarks">Remarks</Label>
                            <Textarea
                                id="mt-remarks"
                                rows={3}
                                value={form.remarks}
                                onChange={(e) => change("remarks", e.target.value)}
                                placeholder="Notes, context..."
                                data-testid="mytask-remarks-input"
                            />
                        </div>

                        <DialogFooter className="sm:justify-between">
                            {task?.id ? (
                                <Button
                                    type="button"
                                    variant="ghost"
                                    className="text-red-600 hover:bg-red-50 hover:text-red-700"
                                    onClick={() => setConfirmDelete(true)}
                                    data-testid="mytask-delete-button"
                                >
                                    <Trash2 className="mr-1.5 h-4 w-4" /> Delete
                                </Button>
                            ) : (
                                <span />
                            )}
                            <div className="flex gap-2">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => onOpenChange(false)}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    disabled={submitting}
                                    data-testid="mytask-save-button"
                                >
                                    {submitting ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : task ? (
                                        "Save changes"
                                    ) : (
                                        "Create task"
                                    )}
                                </Button>
                            </div>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete this task?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This permanently removes the task from your My Tasks.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={onDelete}
                            className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
                            data-testid="mytask-delete-confirm"
                        >
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
}
